<template>
  <!--begin::Timeline item-->
  <div id="first" class="timeline-item">
    <!--begin::Timeline line-->
    <div class="timeline-line w-40px"></div>
    <!--end::Timeline line-->

    <!--begin::Timeline icon-->
    <div class="timeline-icon symbol symbol-circle symbol-40px me-4">
      <div class="symbol-label "
           :class="{
              'bg-light-success': firstInstance.is_favorable,
              'bg-light-danger': !firstInstance.is_favorable
          }">
        <i class="bi bi-1-circle-fill fs-2 "
           :class="{
              'text-success': firstInstance.is_favorable,
              'text-danger': !firstInstance.is_favorable
          }"></i>
      </div>
    </div>
    <!--end::Timeline icon-->

    <!--begin::Timeline content-->
    <div class="timeline-content mb-10 mb-md-0 mt-n1">
      <!--begin::Timeline heading-->
      <div class="pe-3 mb-5">
        <!--begin::Title-->
        <div id="edit_judicial_precedent_first_instance_drawer_toggle"
             class="text-gray-800 fw-bold fs-4 mb-2">
          <span class="">
            {{ firstInstance.type.name }}
          </span>
          <span class="badge ms-2"
                :class="{
                  'badge-success': firstInstance.is_favorable,
                  'badge-danger': !firstInstance.is_favorable
          }">
            {{ firstInstance.is_favorable ? 'Favorable' : 'Desfavorable' }}
          </span>
        </div>
        <!--end::Title-->
      </div>
      <!--end::Timeline heading-->

      <!--begin::Timeline details-->
      <div class="overflow-auto pb-5">
        <!--begin::Info-->
        <div class="d-flex flex-wrap mb-5">
          <!--begin::Due-->
          <div class="border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-7 mb-3">
            <div class="fs-6 text-gray-800 fw-bold">
              {{ firstInstance.date }}
            </div>
            <div class="fw-semibold text-gray-400">Fecha sentencia</div>
          </div>
          <!--end::Due-->
          <!--begin::Due-->
          <div v-if="firstInstance.date_demand" class="border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-7 mb-3">
            <div class="fs-6 text-gray-800 fw-bold">
              {{ firstInstance.date_demand }}
            </div>
            <div class="fw-semibold text-gray-400">Fecha demanda</div>
          </div>
          <!--end::Due-->
          <div
              v-if="firstInstance.court"
              class="d-flex align-items-center border border-dashed border-gray-300 rounded py-3 px-4 mb-3"
          >
            <div class="min-w-125px border-end border-end-dashed pe-3">
              <div class="fs-6 text-gray-800 fw-bold">
                {{ firstInstance.court.name }}
              </div>
              <div class="fw-semibold text-gray-400">Juzgado</div>
            </div>
            <div class="min-w-125px ms-3">
              <div class="fs-6 text-gray-800 fw-bold">
                {{ firstInstance.court_number }}
              </div>
              <div class="fw-semibold text-gray-400">Nº Juzgado</div>
            </div>
          </div>
          <!--end::Record-->
        </div>
        <!--end::Info-->
        <div class="d-flex flex-wrap mb-5">
          <!--begin::List widget 12-->
          <div class="w-lg-50">
            <h3 class="card-title d-flex align-items-center flex-stack flex-wrap d-grid gap-1 flex-row-fluid">
                <span class="card-label fw-bold text-gray-800">
                  {{ firstInstance.file_url ? 'Adjunto' : 'Sin adjunto'}}
                </span>
            </h3>
            <!--begin::Body-->
            <div v-if="firstInstance.file_url" class="pt-3">
              <!--begin::Item-->
              <div class="d-flex flex-stack">
                <!--begin::Title-->
                <button @click="downloadBlob(currentUser.currentPlan)" class="btn btn-primary">Descargar adjunto</button>
                <!--end::Title-->
              </div>
              <!--end::Item-->
            </div>
          </div>
          <!--end::List widget 12-->
        </div>
        <hr class="border-dashed border-primary">
      </div>
      <!--end::Timeline details-->
    </div>
    <!--end::Timeline content-->
  </div>
  <!--end::Timeline item-->
</template>

<script lang="ts">
import {defineComponent, PropType, ref, nextTick, onMounted, watch} from "vue";
import {JudicialPrecedent, JudicialPrecedentInstance} from "~/types/interfaces/entitites/JudicialPrecedentInterface";
import {Plan} from "~/types/interfaces/entitites/PlanInterface";
import {useUtil} from "~/composables/useUtil";
import {useAuthStore} from "~/stores/auth";
import {storeToRefs} from "pinia";
import {useJudicialPrecedentsClientStore} from "~/stores/entities/client/judicialPrecedentClient";
import {reinitializeComponents} from "~/theme/metronic/plugins/keenthemes";
import {useConfigStore} from "~/stores/config";

export default defineComponent({
  name: "first-instance",
  components: {},
  props: {
    firstInstance: {type: Object as PropType<JudicialPrecedentInstance>, required: true},
    instance: {type: Object as PropType<JudicialPrecedent>, required: true},
  },
  setup(props) {
    const configStore = useConfigStore();
    const route = useRoute();
    const judicialPrecedentsClientStore = useJudicialPrecedentsClientStore();
    const store = useAuthStore();
    const {currentUser} = storeToRefs(store);
    let automaticDownloads = ref<number>(0);
    const {setPageTitle} = configStore;

    onMounted(() => {
      nextTick(() => {
        checkAutomaticDownload();
      });
    });

    const checkAutomaticDownload = () => {
      if (currentUser.value.currentPlan && route.query.download !== undefined) {
        if (automaticDownloads.value === 0 && route.query.download === '1') {
          downloadBlob(currentUser.value.currentPlan)
          automaticDownloads.value += +1;
          setPageTitle(props.firstInstance.file_name);
        }
      }
    };

    const canDownload = (plan: Plan): boolean => {
      return plan.name === 'Pleno+' || plan.name === 'Pleno vulnerable';
    };

    const downloadBlob = async (plan: Plan) => {
      if (canDownload(plan)) {
        useUtil().showLoading();

        // Perform an HTTP request to fetch the Blob data
        const response = await judicialPrecedentsClientStore.download(route.params.hash, props.firstInstance.hash);

        // Create a URL for the Blob data
        const blobURL = window.URL.createObjectURL(response);

        // Create an <a> element to trigger the download
        const downloadLink = document.createElement('a');
        downloadLink.href = blobURL;
        downloadLink.download = props.instance.slug + '_' + props.firstInstance.type.slug + '.pdf';

        // Trigger the download
        downloadLink.click();

        // Clean up by revoking the Blob URL
        window.URL.revokeObjectURL(blobURL);
        useUtil().closeLoading();
        useUtil().defaultDownloadSuccess();
      } else {
        useUtil().defaultError('Permiso denegado', 'Necesitas una suscripción superior para poder descargar estos archivos.');
      }
    };

    return {
      currentUser,
      downloadBlob
    };
  },
});
</script>
